/**
 * Zanetti product and story pages.
 * - <zanetti-view-tabs>: Front / Back / Worn tabs that drive the phone photo carousel.
 * - <zanetti-size-required>: no size is picked until the customer picks one, and Add to
 *   bag (the sticky bar's too) asks for a size instead of adding the first one.
 * - Linked group blocks (the story page's caps and shirts panels) get an accessible name.
 * Loaded once from snippets/zanetti-theme.liquid; does nothing on pages without these.
 */

/* Photo tabs (phone) */
(() => {
  if (customElements.get('zanetti-view-tabs')) return;

  class ZanettiViewTabs extends HTMLElement {
    #controller = new AbortController();

    connectedCallback() {
      const { signal } = this.#controller;
      this.addEventListener('click', this.#onClick, { signal });
      this.closest('.shopify-section')?.addEventListener('slideshow:select', this.#onSelect, { signal });
    }

    disconnectedCallback() {
      this.#controller.abort();
      this.#controller = new AbortController();
    }

    get slideshow() {
      return this.closest('.shopify-section')?.querySelector('media-gallery slideshow-component');
    }

    /** @param {number} index */
    #mark(index) {
      this.querySelectorAll('.z-views__tab').forEach((tab) => {
        tab.setAttribute('aria-pressed', String(Number(tab.getAttribute('data-index')) === index));
      });
    }

    /** @param {MouseEvent} event */
    #onClick = (event) => {
      const tab = event.target instanceof Element ? event.target.closest('.z-views__tab') : null;
      if (!tab) return;
      const index = Number(tab.getAttribute('data-index')) || 0;
      this.#mark(index);
      const slideshow = /** @type {any} */ (this.slideshow);
      if (slideshow && typeof slideshow.select === 'function') slideshow.select(index);
    };

    /** @param {Event} event */
    #onSelect = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('media-gallery')) return;
      const detail = /** @type {any} */ (event).detail;
      if (detail && typeof detail.index === 'number') this.#mark(detail.index);
    };
  }

  customElements.define('zanetti-view-tabs', ZanettiViewTabs);
})();

/* Size required */
(() => {
  if (customElements.get('zanetti-size-required')) return;

  const startedWithVariant = new URL(window.location.href).searchParams.has('variant');
  let customerPicked = false;

  /** @param {Element} el */
  const sectionOf = (el) => el.closest('.shopify-section');

  /** @param {Element | null} section */
  const radiosIn = (section) =>
    section ? Array.from(section.querySelectorAll('variant-picker input[type="radio"]')) : [];

  /** @param {Element | null} section */
  const hasSize = (section) => radiosIn(section).some((input) => input instanceof HTMLInputElement && input.checked);

  class ZanettiSizeRequired extends HTMLElement {
    connectedCallback() {
      const preselected = this.dataset.preselected === 'true';
      if (preselected || startedWithVariant || customerPicked) return;
      // Horizon ticks the first size; start with none ticked instead.
      radiosIn(sectionOf(this)).forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        input.checked = false;
        input.dataset.currentChecked = 'false';
      });
      // The sticky bar names that first size too; show the prompt until a size is picked
      // (Horizon writes the picked size back in when the customer chooses one).
      const stickyVariant = sectionOf(this)?.querySelector('.sticky-add-to-cart__variant');
      const prompt = this.querySelector('.z-size-required__message')?.textContent?.trim();
      if (stickyVariant && prompt) stickyVariant.textContent = prompt;
    }

    showMessage() {
      const message = this.querySelector('.z-size-required__message');
      if (!(message instanceof HTMLElement)) return;
      message.hidden = false;
      // Re-announce on repeated presses
      message.textContent = message.textContent;
      const section = sectionOf(this);
      const first = radiosIn(section)[0];
      if (first instanceof HTMLElement) first.focus({ preventScroll: true });
      // Bring the sizes and the message to the middle of the screen. From the sticky bar
      // they are far above, and at the top edge the header would cover them.
      const picker = section?.querySelector('variant-picker') || message;
      const headerBottom = document.querySelector('.header-section')?.getBoundingClientRect().bottom || 0;
      const inView =
        picker.getBoundingClientRect().top >= headerBottom &&
        message.getBoundingClientRect().bottom <= window.innerHeight - 96;
      if (inView) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      picker.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    hideMessage() {
      const message = this.querySelector('.z-size-required__message');
      if (message instanceof HTMLElement) message.hidden = true;
    }
  }

  customElements.define('zanetti-size-required', ZanettiSizeRequired);

  /** @param {Event} event */
  const guard = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger =
      event.type === 'submit'
        ? target.closest('product-form-component form, form[data-type="add-to-cart-form"]')
        : target.closest('button[name="add"], .sticky-add-to-cart__button');
    if (!trigger) return;
    const section = sectionOf(trigger);
    const guardEl = section?.querySelector('zanetti-size-required');
    if (!(guardEl instanceof ZanettiSizeRequired)) return;
    if (hasSize(section)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.type === 'click') guardEl.showMessage();
  };

  // On window, so the guard runs before Horizon's own click handlers (they listen on the
  // document); otherwise the sticky bar would play its "added" animation with no size.
  window.addEventListener('click', guard, true);
  window.addEventListener('submit', guard, true);

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.type !== 'radio' || !target.closest('variant-picker')) return;
      customerPicked = true;
      const guardEl = sectionOf(target)?.querySelector('zanetti-size-required');
      if (guardEl instanceof ZanettiSizeRequired) guardEl.hideMessage();
    },
    true
  );
})();

/* Linked group blocks: Horizon renders the link as an empty <a> over the group, so it has
   no name for screen readers. Use the group's first line of text (e.g. "The caps"). */
(() => {
  const label = () => {
    document.querySelectorAll('#MainContent a.group-block__link:not([aria-label])').forEach((link) => {
      const text = link.closest('.group-block')?.querySelector('.text-block')?.textContent;
      const name = text ? text.replace(/\s+/g, ' ').trim() : '';
      if (name) link.setAttribute('aria-label', name);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', label, { once: true });
  } else {
    label();
  }
  document.addEventListener('shopify:section:load', label);
})();
